import React from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useCloudAsset } from '../../../lib/services/cloud-asset.service';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
    color: theme.palette.grey[500],
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
    color: theme.palette.error.main,
    border: `1px dashed ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
}));

interface CloudImageProps {
  assetPath: string;
  alt: string;
  width?: string | number;
  height?: string | number;
  style?: React.CSSProperties;
  className?: string;
  loadingText?: string;
  errorText?: string;
  onClick?: () => void;
}

/**
 * Component for displaying images from Google Cloud Storage with loading states
 */
export const CloudImage: React.FC<CloudImageProps> = ({
  assetPath,
  alt,
  width,
  height,
  style = {},
  className,
  loadingText = 'Loading image...',
  errorText = 'Failed to load image',
  onClick,
}) => {
  const classes = useStyles();
  const { url, loading, error } = useCloudAsset(assetPath);

  const containerStyle = {
    width,
    height,
    ...style,
  };

  if (loading) {
    return (
      <Box className={`${classes.container} ${className || ''}`} style={containerStyle}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={24} />
          <Typography variant="caption" style={{ marginTop: 8 }}>
            {loadingText}
          </Typography>
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={`${classes.container} ${className || ''}`} style={containerStyle}>
        <div className={classes.errorContainer}>
          <Typography variant="caption">{errorText}</Typography>
          <Typography variant="caption" color="textSecondary">
            {assetPath}
          </Typography>
        </div>
      </Box>
    );
  }

  if (!url) {
    return null;
  }

  return (
    <Box className={`${classes.container} ${className || ''}`} style={containerStyle}>
      <img
        src={url}
        alt={alt}
        className={classes.image}
        style={{ width, height, cursor: onClick ? 'pointer' : 'default' }}
        onClick={onClick}
      />
    </Box>
  );
};